import { fg } from '@confluence/feature-gating';
import {
	CURRENT_STATUS,
	DRAFT_STATUS,
	ContentStatus,
} from '@confluence/page-utils/entry-points/enums';
import { useSessionData } from '@confluence/session-data';
import type { TargetContentType } from '@confluence/entity-operations';
import { useCanPerformContentOperation, OperationTypes } from '@confluence/entity-operations';
import { usePageContentId } from '@confluence/page-context';

type useContentOwnerItemDataParams = {
	ownerAccountId?: string | null;
	isCurrentUserSpaceAdmin: boolean;
	contentType?: string | null;
	contentStatus?: ContentStatus | string | null;
};

export const useIsChangeOwnerAvailable = ({
	ownerAccountId,
	isCurrentUserSpaceAdmin,
	contentType,
	contentStatus,
}: useContentOwnerItemDataParams): boolean => {
	const { userId: currentUserId } = useSessionData();

	const [contentId] = usePageContentId();
	const [canUpdateContent] = useCanPerformContentOperation({
		contentId: contentId ?? '',
		contentType: contentType as TargetContentType,
		operation: OperationTypes.UPDATE,
		status: contentStatus === ContentStatus.DRAFT ? DRAFT_STATUS : CURRENT_STATUS,
	});

	if (!contentType || !contentStatus) {
		return false;
	}

	const isNotBlogpost = contentType !== 'blogpost';
	const isNotDraft = contentStatus !== ContentStatus.DRAFT;
	const isNotArchived = contentStatus !== ContentStatus.ARCHIVED;
	// true if FF is off or canUpdateContent is undefined
	const hasWriteAccess = fg('confluence_frontend_content_wrapper')
		? canUpdateContent ?? true
		: true;
	const isContentOwner = ownerAccountId === currentUserId;

	return (
		isNotBlogpost &&
		isNotDraft &&
		isNotArchived &&
		hasWriteAccess &&
		(isContentOwner || isCurrentUserSpaceAdmin)
	);
};
